import { SectionTitle, Sub, RefLink, P, ScopePill, FlowSteps } from "./GuideHelpers";

export const SectionOverview = () => (
  <>
    <SectionTitle id="overview">Platform Overview</SectionTitle>
    <P>
      Signal by Recom is an Amazon SEO intelligence platform. It helps brands understand how their products
      rank for keywords, how they compare to competitors, and what content optimizations will drive
      measurable improvements in organic visibility and sales.
    </P>
    <P>
      The platform is organized around a <strong>brand → product → keyword</strong> hierarchy.
      Every user starts by selecting a brand, then manages individual products (Hero ASINs),
      and within each product accesses keyword-level tools like Strategy, Keyword Bank,
      Landscapes, A/B Tests, and Search Terms.
    </P>

    <Sub>Core Concepts</Sub>
    <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground mb-4">
      <li><RefLink to="navigation-model">Brand</RefLink> — The top-level entity. All data is filtered by the selected brand. Switch brands using the selector in the sidebar.</li>
      <li><RefLink to="products">Hero ASIN</RefLink> — A product (Amazon ASIN) that you actively manage and optimize. This is the product you "own" and want to improve.</li>
      <li><RefLink to="keyword-landscapes">Competitor ASIN</RefLink> — Products that compete with your Hero ASIN for the same keywords. Tracked for benchmarking.</li>
      <li><RefLink to="keyword-bank">Keyword</RefLink> — A search term shoppers type into Amazon. Keywords are the atomic unit of SEO strategy.</li>
      <li><RefLink to="keyword-landscapes">Landscape</RefLink> — A snapshot of how your Hero ASIN and competitors rank across a group of keywords at a point in time.</li>
      <li><RefLink to="brand-signals">Brand Signal</RefLink> — A validated, brand-level hypothesis about what drives conversion for a specific brand, surfaced by aggregating that brand's A/B test outcomes across products. Lives in the brand-scoped Brand Signals page.</li>
      <li><RefLink to="bs-category">Category Signal</RefLink> — A cross-brand pattern aggregated by product category (e.g., facial SPF, body SPF). Surfaced in the portfolio-scoped Category Signals view to reveal what consistently wins across multiple brands in the same category.</li>
    </ul>

    <Sub>Information Architecture</Sub>
    <div className="rounded-sm border border-border bg-surface-tint-soft p-4 text-xs text-muted-foreground font-mono leading-relaxed mb-4">
      <div className="text-foreground font-semibold mb-1">Brand-scoped (driven by Brand selector)</div>
      <div>Brand (selected in sidebar)</div>
      <div className="pl-4">├─ Brand Overview — brand-wide KPIs & activity</div>
      <div className="pl-4">├─ Products Hub — grid of all Hero ASINs</div>
      <div className="pl-8">└─ Product Detail (per ASIN)</div>
      <div className="pl-12">├─ SEO Insights — AI recommendations</div>
      <div className="pl-12">├─ Keywords — master keyword list</div>
      <div className="pl-12">├─ Landscapes — competitive snapshots</div>
      <div className="pl-12">├─ A/B Tests — content experiments</div>
      <div className="pl-12">├─ Search Terms — backend search terms</div>
      <div className="pl-12">└─ Content Scorecard — per-product listing audit</div>
      <div className="pl-4">├─ Brand Signals — brand-level hypotheses</div>
      <div className="pl-4">└─ Rank Tracker — keyword rank monitoring</div>
      <div className="text-foreground font-semibold mt-3 mb-1">Portfolio-scoped (across all brands)</div>
      <div>Workspace</div>
      <div className="pl-4">├─ Analytics</div>
      <div className="pl-8">├─ Brand Comparison</div>
      <div className="pl-8">├─ All Brand Signals</div>
      <div className="pl-8">├─ Category Signals</div>
      <div className="pl-8">└─ Signal ROI</div>
      <div className="pl-4">└─ Run a Scorecard — standalone listing audit</div>
    </div>

    <SectionTitle id="navigation-model">Navigation Model</SectionTitle>
    <P>
      Signal's left sidebar is split into two zones by a divider line. The zone an item lives in
      determines whether it responds to the Brand selector at the top of the sidebar or aggregates
      data across your entire portfolio. Knowing which zone you're in is the fastest way to
      understand what data you're looking at.
    </P>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div className="rounded-md border border-primary/30 bg-primary-tint-subtle p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-lg font-semibold text-foreground">Brand-scoped views</div>
            <div className="text-xs text-muted-foreground mt-0.5">Above the divider</div>
          </div>
          <ScopePill scope="brand" />
        </div>
        <P>
          Driven entirely by the <strong>Brand selector</strong> at the top of the sidebar. Switching
          brands swaps the entire dataset on these pages — products, keywords, signals, and ranks all
          belong to the currently selected brand.
        </P>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>Brand Overview</li>
          <li>Products</li>
          <li>Brand Signals</li>
          <li>Rank Tracker</li>
        </ul>
      </div>

      <div className="rounded-md border border-trend-positive/30 bg-status-success-soft p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-lg font-semibold text-foreground">Portfolio-scoped views</div>
            <div className="text-xs text-muted-foreground mt-0.5">Below the divider</div>
          </div>
          <ScopePill scope="portfolio" />
        </div>
        <P>
          Aggregate data across <strong>all brands in the workspace</strong>. These pages ignore the
          Brand selector at the top of the sidebar. Where filtering by brand is useful, the page
          provides its own multi-select brand filter (e.g., the Analytics pages).
        </P>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>Analytics — Brand Comparison, All Brand Signals, Category Signals, Signal ROI</li>
          <li>Run a Scorecard</li>
          <li>Notifications</li>
          <li>Add a Brand</li>
        </ul>
      </div>
    </div>

    <P>
      Throughout this guide, every section header carries a <ScopePill scope="brand" /> or{" "}
      <ScopePill scope="portfolio" /> pill so you can tell at a glance which zone the page belongs to.
    </P>

    <SectionTitle id="getting-started">Getting Started</SectionTitle>
    <P>
      When a new user first arrives, they should follow this sequence to set up their workspace:
    </P>
    <div className="mb-4">
      <FlowSteps
        showLegend
        items={[
          { actor: "user", label: "Create or select a Brand in the sidebar" },
          { actor: "user", label: "Add Hero ASINs (your products) via Products Hub" },
          { actor: "signal", label: "Keyword bank auto-populates from SQP report and Signal data lake" },
          { actor: "user", label: "Run a Content Scorecard to audit listing quality" },
          { actor: "user", label: "Generate a Landscape to benchmark vs. competitors" },
          { actor: "user", label: "Review SEO Insights for AI-powered recommendations" },
          { actor: "user", label: "Add keywords to Rank Tracker for ongoing monitoring" },
        ]}
      />
    </div>
    <P>
      Once the foundation is in place, the ongoing workflow involves reviewing the Dashboard for
      changes, running A/B Tests on content, and regenerating Landscapes to measure progress.
    </P>

    <Sub>Empty States</Sub>
    <P>
      When a page has no data yet (e.g., no Hero ASINs added), it displays a <strong>skeleton preview</strong> of
      the page layout behind a call-to-action overlay. This gives you visual context for what the page will look
      like once populated, and guides you toward the next setup step.
    </P>
  </>
);
